import React, { useEffect, useState, useContext } from "react";
import { getApprovedForms } from "../../api/Api";
import Loader from "../../Loader/Loader";
import { Search } from "lucide-react";
import { IoIosCloseCircleOutline } from "react-icons/io";
import Pagination from "../../Loader/Pagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faCalendarAlt, faFilter, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "../../utils/AuthContext";
import { generateAcademicYears } from "../../utils/academicYearUtils";

const ApprovedForms = () => {
  const [approvedForms, setApprovedForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredForms, setFilteredForms] = useState([]);
  const [selectedAdmissionFor, setSelectedAdmissionFor] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { academicYear, setAcademicYear } = useContext(AuthContext);
  const academicYears = generateAcademicYears();

  useEffect(() => {
    const fetchApprovedForms = async () => {
      setLoading(true);
      try {
        const response = await getApprovedForms(academicYear);
        if (response.success) {
          setApprovedForms(response.data.reverse());
          setFilteredForms(response.data);
        } else {
          setApprovedForms([]);
          setFilteredForms([]);
          console.log("No approved forms found.");
        }
      } catch (error) {
        console.error("Error fetching approved forms: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchApprovedForms();
  }, [academicYear]);

  const handleAdmissionForChange = (admissionFor) => {
    setSelectedAdmissionFor(admissionFor);
    setCurrentPage(1); // Reset to the first page when filtering
  };

  useEffect(() => {
    const filtered = approvedForms.filter((form) => {
      const fullName = form.particularsOfChild?.fullName?.toLowerCase() || "";
      const invoice = form.invoiceNo?.toLowerCase() || "";
      const admission = form.admissionFor?.toLowerCase() || "";
      const query = searchQuery.toLowerCase();

      const matchesSearch =
        fullName.includes(query) || invoice.includes(query) || admission.includes(query);

      const admissionDate = form.createdAt ? new Date(form.createdAt.slice(0, 10)) : null;
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      const matchesDate =
        (!start || (admissionDate && admissionDate >= start)) &&
        (!end || (admissionDate && admissionDate <= end));

      const matchesAdmissionFor =
        selectedAdmissionFor === "All" || form.admissionFor === selectedAdmissionFor;

      return matchesSearch && matchesDate && matchesAdmissionFor;
    });

    setFilteredForms(filtered);
    setCurrentPage(1);
  }, [searchQuery, startDate, endDate, selectedAdmissionFor, approvedForms]);

  const handleViewStudent = (form) => {
    setSelectedStudent(form);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStudent(null);
  };

  const options = ["All", ...new Set(approvedForms.map((form) => form.admissionFor))];

  const totalPages = Math.ceil(filteredForms.length / itemsPerPage);
  const currentData = filteredForms.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatDateDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="p-4 max-w-screen-xl mx-auto font-poppins">
      {/* Top Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-5">

        {/* Top Row */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">

          {/* Title and Filter Info */}
          <div className="flex items-center gap-6">
            <div>
              <h1 className="text-2xl font-bold text-[#16bab1]">
                Students
              </h1>
              <p className="text-sm text-gray-500">
                Manage all student records
              </p>
            </div>

            {/* Academic Year Filter */}
            <div className="hidden sm:flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
              <FontAwesomeIcon icon={faFilter} className="text-[#16bab1] w-3 h-3" />
              <div className="flex flex-col">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Year</label>
                <select
                  value={academicYear}
                  onChange={(e) => setAcademicYear(e.target.value)}
                  className="bg-transparent text-sm font-semibold text-gray-700 outline-none pr-4 appearance-none cursor-pointer"
                >
                  <option value="">Overall View</option>
                  {academicYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative w-full xl:w-[280px]">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />

            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search students..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-[#16bab1]"
            />
          </div>

          {/* Date Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
              <div className="flex flex-col">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">From</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-transparent text-sm font-medium outline-none"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
              <div className="flex flex-col">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">To</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-transparent text-sm font-medium outline-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Class Filters */}
        <div className="flex flex-wrap gap-2 mt-5">

          {/* <button
      onClick={() => handleAdmissionForChange("")}
      className={`px-4 py-2 rounded-full border text-sm font-medium transition-all duration-200
        ${
          selectedAdmissionFor === ""
            ? "bg-[#16bab1] text-white border-[#16bab1] shadow-sm"
            : "bg-white text-gray-700 border-gray-300 hover:border-[#16bab1] hover:text-[#16bab1]"
        }`}
    >
     
    </button> */}

          {options.map((option) => (
            <button
              key={option}
              onClick={() => handleAdmissionForChange(option)}
              className={`px-4 py-2 rounded-full border text-sm font-medium capitalize transition-all duration-200
          ${selectedAdmissionFor === option
                  ? "bg-[#16bab1] text-white border-[#16bab1] shadow-sm"
                  : "bg-white text-gray-700 border-gray-300 hover:border-[#16bab1] hover:text-[#16bab1]"
                }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <Loader />
      ) : currentData.length === 0 ? (
        <div className="bg-white p-6 rounded-xl shadow-sm text-[#f36743] text-center text-lg font-medium mt-8">
          No approved forms found matching your criteria.
        </div>
      ) : (
        <div className="bg-white p-6 rounded-xl shadow-sm ">
          <table className="min-w-full divide-y divide-gray-200 border-l-emerald-300">
            <thead className="bg-[#16bab1]">
              <tr>
                <TableHead >S.No</TableHead>
                <TableHead>Admission No</TableHead>
                <TableHead>Student Name</TableHead>
                <TableHead>Year</TableHead>
                <TableHead>Contact</TableHead>

                <TableHead>Parents</TableHead>
                {/* <TableHead>E-mail</TableHead> */}
                <TableHead>Balance</TableHead>
                {/* <TableHead>Admission Date</TableHead> */}
                <TableHead>Actions</TableHead>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentData.map((form, index) => (
                <tr key={form._id} className="hover:bg-gray-50 transition-colors duration-150 border-cyan-100">
<TableCell>

  {filteredForms.length -

    ((currentPage - 1) * itemsPerPage + index)}

</TableCell>
                  <TableCell>{form.invoiceNo}</TableCell>
                  <TableCell>{form.particularsOfChild?.fullName}</TableCell>
                  <TableCell>{form.academicYear}</TableCell>
                  <TableCell>
                    <div >F-{form.particularsOfParents?.FatherMobile || form.particularsOfParents?.FatherMobile || 'N/A'}</div>
                    <div >M-{form.particularsOfParents?.MotherMobile || form.particularsOfParents?.MotherMobile || 'N/A'}</div>
                  </TableCell>
                  {/* <TableCell>{form.particularsOfParents?.MotherMobile}</TableCell> */}
                  <TableCell>
                    <div className="mb-1">F-{form.particularsOfParents?.FatherName || 'N/A'}</div>
                    <div>M-{form.particularsOfParents?.MotherName || 'N/A'}</div>
                  </TableCell>
                  {/* <TableCell>
                    <div className="mb-1">F-{form.particularsOfParents?.FatherEmail || 'N/A'}</div>
                    <div>M-{form.particularsOfParents?.MotherEmail || 'N/A'}</div>
                  </TableCell> */}
                  <TableCell>₹{form.feeAmount - form.paidFee}</TableCell>
                  {/* <TableCell>{formatDateDisplay(form.createdAt)}</TableCell> */}
                  <TableCell>
                    <button
                      onClick={() => handleViewStudent(form)}
                      className="text-blue-600 hover:text-blue-800 flex items-center justify-center mx-auto p-1 rounded-full hover:bg-blue-100 transition-colors"
                      title="View Student Details"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {isModalOpen && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 md:p-8 relative">
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-red-600 transition-colors"
              title="Close"
            >
              <IoIosCloseCircleOutline size={30} />
            </button>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 border-b pb-4">
              Student Details
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 text-sm">
              <DetailItem label="Full Name" value={selectedStudent.particularsOfChild?.fullName} />
              <DetailItem label="Nationality" value={selectedStudent.particularsOfChild?.nationality} />
              <DetailItem label="Date of Birth" value={formatDateDisplay(selectedStudent.particularsOfChild?.dob)} />
              <DetailItem label="Blood Group" value={selectedStudent.particularsOfChild?.blood} />
              <DetailItem label="Gender" value={selectedStudent.particularsOfChild?.gender} />
              <DetailItem label="Age" value={selectedStudent.particularsOfChild?.age} />
              <DetailItem label="Father Name" value={selectedStudent.particularsOfParents?.FatherName} />
              <DetailItem label="Father Mobile" value={selectedStudent.particularsOfParents?.FatherMobile} />
              <DetailItem label="Father Email" value={selectedStudent.particularsOfParents?.FatherEmail} />
              <DetailItem label="Mother Name" value={selectedStudent.particularsOfParents?.MotherName} />
              <DetailItem label="Mother Mobile" value={selectedStudent.particularsOfParents?.MotherMobile} />
              <DetailItem label="Mother Email" value={selectedStudent.particularsOfParents?.MotherEmail} />
              <DetailItem label="House No" value={selectedStudent.particularsOfChild?.houseNo} />
              <DetailItem label="Building Name" value={selectedStudent.particularsOfChild?.buildingName} />
              <DetailItem label="Street Name" value={selectedStudent.particularsOfChild?.streetName} />
              <DetailItem label="City" value={selectedStudent.particularsOfChild?.city} />
              <DetailItem label="Pincode" value={selectedStudent.particularsOfChild?.pincode} />
              <DetailItem label="Register No" value={selectedStudent.invoiceNo} />
              <DetailItem label="Admission Date" value={formatDateDisplay(selectedStudent.createdAt)} />
              <DetailItem label="Admission For" value={selectedStudent.admissionFor} />
              <DetailItem label="Fee Amount" value={`₹${selectedStudent.feeAmount}`} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TableHead = ({ children }) => (
  <th className="border px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-white">
    {children}
  </th>
);

const TableCell = ({ children }) => (
  <td className="border px-4 py-3 whitespace-nowrap text-sm text-gray-700">
    {children}
  </td>
);

const DetailItem = ({ label, value }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm">
    <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
    <p className="text-sm font-semibold text-gray-800">{value || "N/A"}</p>
  </div>
);

export default ApprovedForms;
